import React, { ElementType, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, styled, type SxProps, type Theme, useTheme } from '@mui/material';
import { Box } from '@mui/material';
import _get from 'lodash/get';
import _xor from 'lodash/xor';
import { mergeSx } from 'merge-sx';
import { type TreeTogglerTemplateOptions } from 'primereact/tree';
import { type TreeNode } from 'primereact/treenode';
import { TreeSelect, type TreeSelectChangeEvent, type TreeSelectSelectionKeysType } from 'primereact/treeselect';

import { FolderType } from '@/api/generated.ts';
import ChevronDownIcon from '@/assets/icons/chevron-down.svg?react';
import withFormControl from '@/hoc/withFormControl';
import { useOrganizationStructure } from '@/hooks/useOrganizationStructure.tsx';
import { borders, transforms, transitions } from '@/theme/constants.ts';
import typography from '@/theme/typography.ts';

import { buildTreeNodeOptions, createLocationTreeValue, createNodeMap } from './locationTreeUtils.tsx';

interface LocationTreeSelectFieldProps {
  value?: string[] | string;
  onChange?: (value: string[] | string) => void;
  sx?: SxProps<Theme>;
  isMulti?: boolean;
  organizationSlug?: string;
  placeholder?: string;
  allowedTypes?: FolderType[];
  disabled?: boolean;
  togglerIconComponent?: ElementType<LocationTreeSelectTogglerIconProps>;
  dropdownIconComponent?: ElementType<LocationTreeSelectDropdownIconProps>;
  error?: string;
}

const LocationTreeSelectField: React.FC<LocationTreeSelectFieldProps> = ({
  value,
  onChange,
  sx,
  isMulti,
  organizationSlug,
  allowedTypes,
  togglerIconComponent = LocationTreeSelectTogglerIcon,
  dropdownIconComponent = LocationTreeSelectDropdownIcon,
  disabled,
  error,
  ...props
}) => {
  const TogglerIconComponent = togglerIconComponent;
  const DropdownIconComponent = dropdownIconComponent;
  const [isOpen, setIsOpen] = useState(false);
  const { palette } = useTheme();

  const { t } = useTranslation('processes');
  const { recursiveStructure: recursiveOrganizationStructure } = useOrganizationStructure();

  const nodes = useMemo(
    () => recursiveOrganizationStructure && buildTreeNodeOptions(recursiveOrganizationStructure, false, allowedTypes),
    [allowedTypes, recursiveOrganizationStructure]
  );
  const nodeMap = useMemo(() => (nodes ? createNodeMap(nodes) : {}), [nodes]);
  const isEmpty = useMemo(() => !organizationSlug || !Object.keys(nodeMap).length, [nodeMap, organizationSlug]);
  const valueToRender = useMemo(
    () => (isMulti ? createLocationTreeValue(value as string[], nodeMap) : (value as string)),
    [isMulti, nodeMap, value]
  );

  const handleChange = useCallback(
    (e: TreeSelectChangeEvent) => {
      // eslint-disable-next-line i18next/no-literal-string
      const target = (_get(e, 'originalEvent.originalEvent.target') ?? _get(e, 'originalEvent.target')) as
        | HTMLElement
        | undefined;
      // eslint-disable-next-line i18next/no-literal-string
      const activeKey = target?.closest('.p-treenode')?.querySelector('[data-key]')?.getAttribute('data-key') as string;

      const newValues = isMulti ? _xor(value as string[], [activeKey]) : [activeKey as string];
      const newValuesFiltered = newValues
        .map((key: string) => nodeMap[key])
        .filter(node => !allowedTypes || allowedTypes.includes(node?.type as FolderType))
        .map(node => node?.key as string);

      onChange?.(isMulti ? newValuesFiltered : newValuesFiltered[0] ?? null);
    },
    [allowedTypes, isMulti, nodeMap, onChange, value]
  );

  return (
    <LocationTreeSelectContainer
      direction="row"
      sx={mergeSx<Theme>(
        !isMulti && {
          '& .p-treeselect.p-treeselect-chip .p-treeselect-token': {
            border: 'none'
          }
        },
        {
          '& .LocationItem-icon': {
            width: 16,
            height: 16
          },
          '& .p-treeselect-trigger': {
            width: 32,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          },
          '& .p-treeselect-trigger > .MuiBox-root': {
            height: 16
          }
        },
        !!error && {
          '& .p-treeselect': {
            borderWidth: borders._1px,
            borderColor: palette.error.main,
            '&:hover': {
              borderColor: palette.error.main
            }
          }
        },
        sx
      )}
    >
      <TreeSelect
        value={valueToRender as TreeSelectSelectionKeysType}
        onChange={handleChange}
        options={nodes as TreeNode[]}
        metaKeySelection={true}
        selectionMode={isMulti ? 'checkbox' : 'single'}
        display="chip"
        placeholder={t('layouts.location.placeholder', { count: isMulti ? 2 : 1 })}
        onShow={() => setIsOpen(true)}
        onHide={() => setIsOpen(false)}
        togglerTemplate={(node: TreeNode, options: TreeTogglerTemplateOptions) => (
          <TogglerIconComponent
            isLeaf={!node?.children?.length}
            expanded={options.expanded}
            onClick={options.onClick}
          />
        )}
        nodeTemplate={(node: TreeNode) => (
          <span className="p-treenode-label" data-key={node.key}>
            {node.icon && <span className={`p-treenode-icon ${node.icon}`} />}
            {node.label}
          </span>
        )}
        dropdownIcon={<DropdownIconComponent isOpen={isOpen} />}
        disabled={isEmpty || disabled}
        {...props}
      />
    </LocationTreeSelectContainer>
  );
};

const LocationTreeSelectContainer = styled(Stack)(({ theme: { spacing, palette } }) => ({
  '& .p-treeselect': {
    width: '100%',
    minHeight: 38
  },
  '& .p-treeselect.p-treeselect-chip .p-treeselect-token': {
    borderRadius: spacing(0.5),
    border: borders.default,
    borderColor: palette.grey[700],
    background: palette.background.default,
    marginRight: 0,
    marginInlineEnd: 4
  },
  '& .p-treeselect .p-treeselect-label.p-placeholder': {
    ...typography.body2,
    color: palette.grey[700],
    paddingLeft: spacing(1.25),
    paddingRight: spacing(1.25)
  },
  width: '100%'
}));

const LocationTreeSelect = withFormControl<string[] | string, string[] | string, LocationTreeSelectFieldProps>(
  LocationTreeSelectField
);

interface LocationTreeSelectTogglerIconProps {
  isLeaf: boolean;
  expanded: boolean;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}

const LocationTreeSelectTogglerIcon: React.FC<LocationTreeSelectTogglerIconProps> = ({ isLeaf, onClick, expanded }) => {
  const { spacing } = useTheme();

  if (isLeaf) {
    return <Box sx={{ width: spacing(1), height: spacing(0.5) }} />;
  }

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 0.5,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '& .ArrowIcon-root': {
          transform: transforms.compose(expanded ? transforms.rotateNone : transforms.rotateMinus90),
          transition: transitions.transform
        }
      }}
    >
      <ChevronDownIcon className="ArrowIcon-root" />
    </Box>
  );
};

export interface LocationTreeSelectDropdownIconProps {
  isOpen: boolean;
}

const LocationTreeSelectDropdownIcon: React.FC<LocationTreeSelectDropdownIconProps> = ({ isOpen }) => (
  <Box
    sx={{
      '& .ArrowIcon-root': {
        transform: transforms.mirrorY(isOpen),
        transition: transitions.transform
      }
    }}
  >
    <ChevronDownIcon className="ArrowIcon-root" />
  </Box>
);

export default LocationTreeSelect;
